import { useEffect, useCallback } from 'react';

interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  action: () => void;
  description: string;
}

interface UseKeyboardShortcutsProps {
  shortcuts: KeyboardShortcut[];
  enabled?: boolean;
}

export const useKeyboardShortcuts = ({ shortcuts, enabled = true }: UseKeyboardShortcutsProps) => {
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Input elementlerinde kısayolları devre dışı bırak
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true') {
      return;
    }

    for (const shortcut of shortcuts) {
      const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase();
      const ctrlMatch = !!shortcut.ctrl === event.ctrlKey;
      const shiftMatch = !!shortcut.shift === event.shiftKey;
      const altMatch = !!shortcut.alt === event.altKey;

      if (keyMatch && ctrlMatch && shiftMatch && altMatch) {
        event.preventDefault();
        shortcut.action();
        break;
      }
    }
  }, [shortcuts, enabled]);

  useEffect(() => {
    if (enabled) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [handleKeyDown, enabled]);
};

// Klavye kısayolları listesi
export const getMindMapKeyboardShortcuts = (actions: {
  onSave: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetView: () => void;
  onAddNode: () => void;
  onDeleteSelected: () => void;
  onCopy: () => void;
  onPaste: () => void;
  onSelectAll: () => void;
  onToggleGrid: () => void;
  onToggleFullscreen: () => void;
  onShowHelp: () => void;
}): KeyboardShortcut[] => [
  {
    key: 's',
    ctrl: true,
    action: actions.onSave,
    description: 'Kaydet'
  },
  {
    key: 'z',
    ctrl: true,
    action: actions.onUndo,
    description: 'Geri Al'
  },
  {
    key: 'y',
    ctrl: true,
    action: actions.onRedo,
    description: 'İleri Al'
  },
  {
    key: 'z',
    ctrl: true,
    shift: true,
    action: actions.onRedo,
    description: 'İleri Al (Alt)'
  },
  {
    key: '=',
    ctrl: true,
    action: actions.onZoomIn,
    description: 'Yakınlaştır'
  },
  {
    key: '-',
    ctrl: true,
    action: actions.onZoomOut,
    description: 'Uzaklaştır'
  },
  {
    key: '0',
    ctrl: true,
    action: actions.onResetView,
    description: 'Görünümü Sıfırla'
  },
  {
    key: 'n',
    ctrl: true,
    action: actions.onAddNode,
    description: 'Yeni Konu Ekle'
  },
  {
    key: 'Delete',
    action: actions.onDeleteSelected,
    description: 'Seçili Öğeyi Sil'
  },
  {
    key: 'Backspace',
    action: actions.onDeleteSelected,
    description: 'Seçili Öğeyi Sil'
  },
  {
    key: 'c',
    ctrl: true,
    action: actions.onCopy,
    description: 'Kopyala'
  },
  {
    key: 'v',
    ctrl: true,
    action: actions.onPaste,
    description: 'Yapıştır'
  },
  {
    key: 'a',
    ctrl: true,
    action: actions.onSelectAll,
    description: 'Tümünü Seç'
  },
  {
    key: 'g',
    ctrl: true,
    action: actions.onToggleGrid,
    description: 'Izgarayı Aç/Kapat'
  },
  {
    key: 'F11',
    action: actions.onToggleFullscreen,
    description: 'Tam Ekran'
  },
  {
    key: 'F1',
    action: actions.onShowHelp,
    description: 'Yardım'
  },
  {
    key: '?',
    shift: true,
    action: actions.onShowHelp,
    description: 'Yardım (Alt)'
  }
];
